'use client';

import React, { useEffect, useRef, useState } from 'react';
import { GraduationCap, Building2, Users, Award } from 'lucide-react';

const stats = [
  { id: 'stat-students', value: 52000, suffix: '+', label: 'Students Counselled', sublabel: 'Successfully admitted in 2025', icon: GraduationCap, color: 'text-[#1a3a8f]' },
  { id: 'stat-colleges', value: 2847, suffix: '+', label: 'Medical Colleges', sublabel: 'Across all states of India', icon: Building2, color: 'text-[#1a3a8f]' },
  { id: 'stat-accuracy', value: 98, suffix: '%', label: 'Satisfaction Rate', sublabel: 'Verified student reviews', icon: Award, color: 'text-warning' },
  { id: 'stat-states', value: 28, suffix: '', label: 'States Covered', sublabel: 'AIQ & State quota counselling', icon: Users, color: 'text-success' },
];

function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, animate }: { stat: typeof stats[0]; animate: boolean }) {
  const count = useCountUp(stat.value, 2000, animate);
  return (
    <div className="card-stat text-center">
      <div className={`w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center glass-card ${stat.color}`}
        style={{ border: '1px solid currentColor', opacity: 1 }}>
        <stat.icon size={22} />
      </div>
      <div className={`text-4xl font-extrabold tabular-nums mb-1 ${stat.color}`}>
        {count.toLocaleString('en-IN')}{stat.suffix}
      </div>
      <div className="text-foreground font-semibold text-base mb-1">{stat.label}</div>
      <div className="text-muted-foreground text-xs">{stat.sublabel}</div>
    </div>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimate(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-[#1a3a8f] uppercase tracking-widest mb-3">By The Numbers</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
            Trusted by Thousands of <span className="gradient-text">NEET Aspirants</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} animate={animate} />
          ))}
        </div>
      </div>
    </section>
  );
}